import { Fragment, useMemo, useState } from 'react'
import { Dialog, Listbox, Transition } from '@headlessui/react'
import {
    MailIcon,
    UserIcon,
    ChevronDownIcon,
    XIcon,
} from '@heroicons/react/outline'
import { Controller, useForm } from 'react-hook-form'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { getCookie } from 'cookies-next'
import { ModalStore } from '../../state/ModalStore'
import { trackFromSubmission } from '../../helpers/utils'
import { shouldSkipLoginRecaptcha } from '../../helpers/shouldSkipLoginRecaptcha'
import CoachingCallModal from './CoachingCallModal'
import CalendarTransitionModal from './CalendarTransitionModal'
import InveloTransitionModal from './InveloTransitionModal'
import OlcTransitionModal from './OlcTransitionModal'
import {
    BOOK_CALL_BUDGET_OPTIONS,
    BOOK_CALL_MAILING_LIST_OPTIONS,
    BOOK_CALL_ROUTE_TYPES,
    buildBookCallHubspotFields,
    buildBookCallMeetingUrl,
    getUnresolvedBookCallConfigItems,
    isPlaceholderValue,
    resolveBookCallRoute,
} from '../../helpers/bookCallRoutes'

const HOMEPAGE_BOOK_CALL_PORTAL_ID = '21994206'
const HOMEPAGE_BOOK_CALL_FORM_ID = '47727be9-31de-4601-b072-563399ef67dc'
const HOMEPAGE_BOOK_CALL_FORM_NAME = 'Homepage Strategy Call Lead'

const INDUSTRY_OPTIONS = [
    'Real Estate',
    'Roofing',
    'HVAC/Plumbing',
    'Solar',
    'Landscaping',
    'Insurance',
    'Other',
]

const getBudgetOptions = (customBudgetOptions) =>
    Array.isArray(customBudgetOptions) && customBudgetOptions.length > 0
        ? customBudgetOptions
        : BOOK_CALL_BUDGET_OPTIONS

const buildHubspotPageUri = (formHash) => {
    const pageUrl = new URL(window.location.href)
    pageUrl.hash = formHash.startsWith('#') ? formHash : `#${formHash}`
    return pageUrl.toString()
}

const normalizeMeetingChoice = (value) =>
    String(value || '')
        .trim()
        .toLowerCase()

const getMeetingBaseUrlFromConfig = ({
    budget,
    industry,
    meetingRoutingConfig,
}) => {
    if (!meetingRoutingConfig) return null

    const {
        lowBudgetValues = [],
        lowBudgetMeetingBaseUrl,
        highBudgetRealEstateMeetingBaseUrl,
        highBudgetOtherMeetingBaseUrl,
        realEstateIndustries = ['Real Estate'],
    } = meetingRoutingConfig

    const normalizedBudget = normalizeMeetingChoice(budget)
    const normalizedIndustry = normalizeMeetingChoice(industry)
    const normalizedLowBudgetValues = lowBudgetValues.map(
        normalizeMeetingChoice,
    )
    const normalizedRealEstateIndustries = realEstateIndustries.map(
        normalizeMeetingChoice,
    )

    if (normalizedLowBudgetValues.includes(normalizedBudget)) {
        return lowBudgetMeetingBaseUrl
    }

    if (normalizedRealEstateIndustries.includes(normalizedIndustry)) {
        return highBudgetRealEstateMeetingBaseUrl
    }

    return highBudgetOtherMeetingBaseUrl
}

async function submitHomepageBookCallLead({
    firstName,
    lastName,
    email,
    industry,
    budget,
    mailingList,
    hubspotPortalId = HOMEPAGE_BOOK_CALL_PORTAL_ID,
    hubspotFormId = HOMEPAGE_BOOK_CALL_FORM_ID,
    formName,
    pageUri,
}) {
    const response = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${hubspotPortalId}/${hubspotFormId}`,
        {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                submittedAt: Date.now(),
                fields: buildBookCallHubspotFields({
                    firstName,
                    lastName,
                    email,
                    industry,
                    budget,
                    mailingList,
                }),
                context: {
                    pageUri,
                    pageName: formName,
                    hutk: getCookie('hubspotutk'),
                },
            }),
        },
    )

    if (!response.ok) {
        let details = null

        try {
            details = await response.json()
        } catch (parseError) {
            details = await response.text()
        }

        const errorMessage =
            details?.errors?.[0]?.message ||
            details?.message ||
            'hubspot-form-submit-failed'
        const error = new Error(errorMessage)
        error.status = response.status
        error.details = details
        throw error
    }
}

function DropdownSelect({
    label,
    placeholder,
    value,
    onChange,
    error,
    options,
    openUpward = false,
    testId,
}) {
    const optionsPositionClass = openUpward
        ? 'bottom-[calc(100%+6px)] top-auto max-sm:top-[calc(100%+6px)] max-sm:bottom-auto'
        : 'top-[calc(100%+6px)]'

    return (
        <div className="flex flex-col gap-2">
            <span className="text-[0.72rem] font-extrabold uppercase tracking-[0.12em] text-[#3F454F]">
                {label}
            </span>
            <Listbox value={value} onChange={onChange}>
                {({ open }) => (
                    <div className="relative overflow-visible">
                        <Listbox.Button
                            data-testid={testId}
                            className={`flex w-full items-center justify-between gap-3 rounded-[12px] border bg-white px-4 py-[14px] text-left text-[0.95rem] transition ${
                                error
                                    ? 'border-[#D14A1F]'
                                    : open
                                    ? 'border-[#ED5C2F] shadow-[0_0_0_3px_rgba(237,92,47,0.18)]'
                                    : 'border-[#F0DDC9]'
                            }`}>
                            <span
                                className={`truncate ${
                                    value ? 'text-[#3F454F]' : 'text-[#9CA3AF]'
                                }`}>
                                {value || placeholder}
                            </span>
                            <ChevronDownIcon
                                className={`h-4 w-4 flex-shrink-0 text-[#ED5C2F] transition-transform ${
                                    open ? 'rotate-180' : ''
                                }`}
                            />
                        </Listbox.Button>
                        <Transition
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0">
                            <Listbox.Options
                                className={`absolute left-0 right-0 z-30 max-h-60 overflow-y-auto overflow-x-hidden rounded-[12px] border border-[#F0DDC9] bg-white p-[6px] pr-2 shadow-[0_18px_40px_rgba(0,0,0,0.18),0_2px_6px_rgba(0,0,0,0.08)] focus:outline-none ${optionsPositionClass}`}>
                                {options.map((option) => (
                                    <Listbox.Option
                                        key={option}
                                        value={option}
                                        className={({ active }) =>
                                            `flex cursor-pointer items-center justify-between gap-[10px] rounded-[8px] px-[14px] py-[11px] text-[0.95rem] ${
                                                active ? 'bg-[#FFF1E8]' : ''
                                            }`
                                        }>
                                        {({ selected }) => (
                                            <>
                                                <span className="text-[#3F454F]">
                                                    {option}
                                                </span>
                                                <span
                                                    className={`font-extrabold text-[#ED5C2F] ${
                                                        selected
                                                            ? 'opacity-100'
                                                            : 'opacity-0'
                                                    }`}>
                                                    ✓
                                                </span>
                                            </>
                                        )}
                                    </Listbox.Option>
                                ))}
                            </Listbox.Options>
                        </Transition>
                    </div>
                )}
            </Listbox>
            {error && (
                <span className="text-[0.78rem] font-semibold text-[#D14A1F]">
                    {error}
                </span>
            )}
        </div>
    )
}

function Field({ label, error, icon, children }) {
    return (
        <label className="flex flex-col gap-2">
            <span className="text-[0.72rem] font-extrabold uppercase tracking-[0.12em] text-[#3F454F]">
                {label}
            </span>
            <span className="relative block">
                {icon}
                {children}
            </span>
            {error && (
                <span className="text-[0.78rem] font-semibold text-[#D14A1F]">
                    {error}
                </span>
            )}
        </label>
    )
}

function HomepageBookCallLeadModal(props) {
    const {
        meetingBaseUrl,
        meetingRoutingConfig,
        meetingTitle = 'Book a Coaching Call',
        budgetOptions,
        hubspotPortalId = HOMEPAGE_BOOK_CALL_PORTAL_ID,
        hubspotFormId = HOMEPAGE_BOOK_CALL_FORM_ID,
        formName = HOMEPAGE_BOOK_CALL_FORM_NAME,
        initialValues = {},
    } = props || {}

    const addModal = ModalStore((state) => state.add)
    const skimModal = ModalStore((state) => state.skim)
    const [submitting, setSubmitting] = useState(false)
    const [submissionError, setSubmissionError] = useState('')
    const { executeRecaptcha } = useGoogleReCaptcha()
    const skipCaptcha = shouldSkipLoginRecaptcha()
    const usesCustomMeetingRouting =
        Boolean(meetingRoutingConfig) ||
        Boolean(meetingBaseUrl) ||
        (Array.isArray(budgetOptions) && budgetOptions.length > 0) ||
        hubspotPortalId !== HOMEPAGE_BOOK_CALL_PORTAL_ID ||
        hubspotFormId !== HOMEPAGE_BOOK_CALL_FORM_ID

    const defaultValues = {
        fullName: '',
        email: '',
        industry: '',
        budget: '',
        mailingList: '',
        ...initialValues,
    }

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues,
    })

    const closeModal = () => {
        skimModal()
    }

    const inputClass = useMemo(
        () =>
            'w-full rounded-[12px] border border-[#F0DDC9] bg-white px-4 py-[14px] pl-[42px] text-[0.95rem] text-[#3F454F] transition focus:border-[#ED5C2F] focus:outline-none focus:ring-4 focus:ring-[rgba(237,92,47,0.18)]',
        [],
    )

    const onSubmit = ({ fullName, email, industry, budget, mailingList }) => {
        const submitLead = async () => {
            setSubmitting(true)
            setSubmissionError('')

            try {
                if (!skipCaptcha && !executeRecaptcha) {
                    throw new Error('recaptcha-not-ready')
                }

                const route = resolveBookCallRoute({
                    industry,
                    budget,
                    mailingList,
                })

                if (!route) {
                    throw new Error('book-call-route-unresolved')
                }

                if (!skipCaptcha) {
                    const recaptchaToken = await executeRecaptcha(
                        'homepage_strategy_call',
                    )
                    const verifyResponse = await fetch(
                        '/api/verify-recaptcha',
                        {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ recaptchaToken }),
                        },
                    )
                    const { success, score } = await verifyResponse.json()

                    if (!success || score < 0.5) {
                        throw new Error('recaptcha-verification-failed')
                    }
                }

                const normalized = fullName.trim().replace(/\s+/g, ' ')
                const parts = normalized.split(' ')
                const firstName = parts.shift() || ''
                const lastName = parts.join(' ')
                const trimmedEmail = email.trim()
                const pageUri = buildHubspotPageUri('homepage-book-call-form')
                const nextInitialValues = {
                    fullName: normalized,
                    email: trimmedEmail,
                    industry,
                    budget,
                    mailingList,
                }

                if (usesCustomMeetingRouting) {
                    const customMeetingBaseUrl =
                        getMeetingBaseUrlFromConfig({
                            budget,
                            industry,
                            meetingRoutingConfig,
                        }) || meetingBaseUrl

                    if (!customMeetingBaseUrl) {
                        throw new Error('book-call-route-unresolved')
                    }

                    await submitHomepageBookCallLead({
                        firstName,
                        lastName,
                        email: trimmedEmail,
                        industry,
                        budget,
                        mailingList,
                        hubspotPortalId,
                        hubspotFormId,
                        formName,
                        pageUri,
                    })

                    trackFromSubmission(formName)
                    if (window.gtag) {
                        window.gtag('event', 'conversion', {
                            send_to: 'AW-10868336784/wuxHCLnL-ooYEJDRtr4o',
                        })
                    }

                    const meetingUrl = buildBookCallMeetingUrl({
                        meetingBaseUrl: customMeetingBaseUrl,
                        firstName,
                        lastName,
                        email: trimmedEmail,
                        industry,
                        budget,
                    })

                    skimModal()
                    addModal(
                        <CoachingCallModal
                            title={meetingTitle}
                            meetingUrl={meetingUrl}
                        />,
                    )
                    return
                }

                await submitHomepageBookCallLead({
                    firstName,
                    lastName,
                    email: trimmedEmail,
                    industry,
                    budget,
                    mailingList,
                    hubspotPortalId,
                    hubspotFormId,
                    formName,
                    pageUri,
                })

                trackFromSubmission(formName)
                if (window.gtag) {
                    window.gtag('event', 'conversion', {
                        send_to: 'AW-10868336784/wuxHCLnL-ooYEJDRtr4o',
                    })
                }

                if (isPlaceholderValue(route.destinationUrl)) {
                    throw new Error(
                        `placeholder-destination:${route.destinationUrl}`,
                    )
                }

                if (route.type === BOOK_CALL_ROUTE_TYPES.TRANSITION_MODAL) {
                    skimModal()
                    if (route.transitionModal === 'olc') {
                        addModal(
                            <OlcTransitionModal
                                olmUrl={route.destinationUrl}
                                onChangeAnswers={() =>
                                    addModal(
                                        <HomepageBookCallLeadModal
                                            meetingTitle={meetingTitle}
                                            formName={formName}
                                            initialValues={nextInitialValues}
                                        />,
                                    )
                                }
                            />,
                        )
                    } else {
                        const onboardingMeetingUrl = buildBookCallMeetingUrl({
                            meetingBaseUrl:
                                route.primaryCtaUrl || route.destinationUrl,
                            firstName,
                            lastName,
                            email: trimmedEmail,
                            industry,
                            budget,
                            mailingList,
                        })

                        addModal(
                            <InveloTransitionModal
                                inveloUrl={route.destinationUrl}
                                onboardingUrl={onboardingMeetingUrl}
                                olmUrl={route.secondaryDestinationUrl}
                            />,
                        )
                    }
                    return
                }

                if (route.type === BOOK_CALL_ROUTE_TYPES.EXTERNAL_REDIRECT) {
                    skimModal()
                    window.location.assign(route.destinationUrl)
                    return
                }

                const meetingUrl = buildBookCallMeetingUrl({
                    meetingBaseUrl: route.destinationUrl,
                    firstName,
                    lastName,
                    email: trimmedEmail,
                    industry,
                    budget,
                })

                skimModal()
                addModal(
                    <CalendarTransitionModal
                        firstName={firstName}
                        onContinue={() =>
                            addModal(
                                <CoachingCallModal
                                    title={meetingTitle}
                                    meetingUrl={meetingUrl}
                                />,
                            )
                        }
                        onChangeAnswers={() =>
                            addModal(
                                <HomepageBookCallLeadModal
                                    meetingTitle={meetingTitle}
                                    formName={formName}
                                    initialValues={nextInitialValues}
                                />,
                            )
                        }
                    />,
                )
            } catch (error) {
                console.error('Homepage strategy call submission failed', {
                    message: error.message,
                    status: error.status,
                    details: error.details,
                    unresolvedBookCallConfig:
                        getUnresolvedBookCallConfigItems(),
                })

                if (error.message === 'recaptcha-not-ready') {
                    setSubmissionError(
                        'reCAPTCHA is still loading. Please try again.',
                    )
                } else if (error.message === 'recaptcha-verification-failed') {
                    setSubmissionError(
                        'reCAPTCHA verification failed. Please try again.',
                    )
                } else if (error.message === 'book-call-route-unresolved') {
                    setSubmissionError(
                        'We could not determine the right next step. Please try again.',
                    )
                } else if (
                    error.message?.startsWith('placeholder-destination:')
                ) {
                    setSubmissionError(
                        'This booking path is not configured yet. Please contact the team for help.',
                    )
                } else if (
                    typeof error.message === 'string' &&
                    error.message !== 'hubspot-form-submit-failed'
                ) {
                    setSubmissionError(error.message)
                } else {
                    setSubmissionError(
                        'We could not save your details right now. Please try again.',
                    )
                }

                setSubmitting(false)
            }
        }

        submitLead()
    }

    return (
        <Dialog
            as="div"
            className="fixed inset-0 z-[60] overflow-y-auto"
            open={true}
            onClose={closeModal}>
            <div className="flex min-h-screen items-center justify-center px-4 py-6 text-center sm:py-10">
                <div className="fixed inset-0 bg-black/25 backdrop-blur-[2px]" />
                <div className="fixed inset-0 bg-black/25" />
                <div className="relative z-10 w-[min(94vw,720px)] max-h-[96vh] overflow-y-auto rounded-[24px] bg-[#FFF6EE] p-0 text-left shadow-[0_18px_40px_rgba(0,0,0,0.22),0_2px_10px_rgba(0,0,0,0.08)] transition-all">
                    <button
                        type="button"
                        onClick={closeModal}
                        aria-label="Close"
                        className="absolute right-4 top-4 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-white/10 text-xl leading-none text-[#ED5C2F] transition-colors hover:bg-white/20">
                        <XIcon className="h-5 w-5 stroke-2 text-[#ED5C2F]" />
                    </button>

                    <div className="relative overflow-visible rounded-[24px] px-11 pb-9 pt-12 max-sm:px-6 max-sm:pt-10">
                        <svg
                            aria-hidden="true"
                            width="164"
                            height="124"
                            viewBox="0 0 164 124"
                            className="pointer-events-none absolute right-8 top-6 max-sm:hidden"
                            xmlns="http://www.w3.org/2000/svg">
                            <g transform="rotate(-8 82 62)">
                                <rect
                                    x="20"
                                    y="26"
                                    width="124"
                                    height="76"
                                    rx="6"
                                    fill="#FFFBF6"
                                    stroke="#F2D8C0"
                                    strokeWidth="1.2"></rect>
                                <path
                                    d="M20 32 L82 72 L144 32"
                                    fill="none"
                                    stroke="#F2D8C0"
                                    strokeWidth="1.2"></path>
                                <rect
                                    x="118"
                                    y="38"
                                    width="20"
                                    height="20"
                                    fill="#ED5C2F"></rect>
                                <line
                                    x1="34"
                                    y1="86"
                                    x2="90"
                                    y2="86"
                                    stroke="#F2D8C0"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"></line>
                                <line
                                    x1="34"
                                    y1="94"
                                    x2="76"
                                    y2="94"
                                    stroke="#F2D8C0"
                                    strokeWidth="1.4"
                                    strokeLinecap="round"></line>
                            </g>
                        </svg>

                        <div className="relative max-w-[520px]">
                            <div className="mb-[10px] text-[0.78rem] font-extrabold uppercase tracking-[0.16em] text-[#ED5C2F]">
                                Get started
                            </div>
                            <div className="text-[1.95rem] font-extrabold leading-[1.1] tracking-[-0.015em] text-[#3F454F] max-sm:text-[1.7rem]">
                                Tell us a bit, then pick a time.
                            </div>
                        </div>

                        <form
                            id="homepage-book-call-form"
                            name="homepage-book-call-form"
                            noValidate
                            onSubmit={handleSubmit(onSubmit)}
                            className="relative mt-7 grid grid-cols-2 gap-x-4 gap-y-[18px] overflow-visible text-left max-sm:grid-cols-1">
                            <Field
                                label="Full Name"
                                error={errors.fullName?.message}
                                icon={
                                    <UserIcon className="absolute left-[14px] top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#ED5C2F]" />
                                }>
                                <input
                                    data-testid="book-call-full-name"
                                    {...register('fullName', {
                                        required:
                                            'Please enter your first and last name.',
                                        validate: (value) => {
                                            const parts = value
                                                .trim()
                                                .split(/\s+/)
                                                .filter(Boolean)
                                            return (
                                                parts.length >= 2 ||
                                                'Please enter your first and last name.'
                                            )
                                        },
                                    })}
                                    placeholder="First and last name"
                                    className={inputClass}
                                />
                            </Field>

                            <Field
                                label="Email"
                                error={errors.email?.message}
                                icon={
                                    <MailIcon className="absolute left-[14px] top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#ED5C2F]" />
                                }>
                                <input
                                    data-testid="book-call-email"
                                    {...register('email', {
                                        required:
                                            'Please enter a complete email address.',
                                        pattern: {
                                            value: /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/,
                                            message:
                                                'Please enter a complete email address.',
                                        },
                                    })}
                                    type="email"
                                    placeholder="you@company.com"
                                    className={inputClass}
                                />
                            </Field>

                            <Controller
                                control={control}
                                name="industry"
                                rules={{
                                    required: 'Please select an industry.',
                                }}
                                render={({ field }) => (
                                    <DropdownSelect
                                        label="Industry"
                                        placeholder="Select your industry"
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={errors.industry?.message}
                                        options={INDUSTRY_OPTIONS}
                                        openUpward
                                        testId="book-call-industry"
                                    />
                                )}
                            />

                            <Controller
                                control={control}
                                name="budget"
                                rules={{
                                    required: 'Please select a range.',
                                }}
                                render={({ field }) => (
                                    <DropdownSelect
                                        label="Monthly Marketing Budget"
                                        placeholder="Select a range"
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={errors.budget?.message}
                                        options={getBudgetOptions(
                                            budgetOptions,
                                        )}
                                        openUpward
                                        testId="book-call-budget"
                                    />
                                )}
                            />

                            {!usesCustomMeetingRouting && (
                                <div className="col-span-full">
                                    <Controller
                                        control={control}
                                        name="mailingList"
                                        rules={{
                                            required:
                                                'Please select your mailing list status.',
                                        }}
                                        render={({ field }) => (
                                            <DropdownSelect
                                                label="Mailing List"
                                                placeholder="Select your mailing list status"
                                                value={field.value}
                                                onChange={field.onChange}
                                                error={
                                                    errors.mailingList?.message
                                                }
                                                options={
                                                    BOOK_CALL_MAILING_LIST_OPTIONS
                                                }
                                                testId="book-call-mailing-list"
                                            />
                                        )}
                                    />
                                </div>
                            )}

                            <button
                                type="submit"
                                data-testid="book-call-submit"
                                disabled={submitting}
                                className="col-span-full mt-2 rounded-full bg-[linear-gradient(135deg,#ED5C2F,#F39E5B)] px-7 py-[18px] text-base font-bold text-white shadow-[0_8px_18px_rgba(237,92,47,0.30)] transition hover:shadow-[0_10px_24px_rgba(237,92,47,0.42)] disabled:cursor-not-allowed disabled:opacity-70">
                                {submitting
                                    ? 'Submitting...'
                                    : 'Book my call →'}
                            </button>

                            {submissionError && (
                                <div className="col-span-full text-sm font-semibold text-[#D14A1F]">
                                    {submissionError}
                                </div>
                            )}
                        </form>

                        <div className="mt-6 flex items-center justify-center gap-2 border-t border-[#F0DDC9] pt-[18px] text-[0.8rem] text-[#9CA3AF]">
                            <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#9CA3AF"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <rect
                                    x="4"
                                    y="11"
                                    width="16"
                                    height="10"
                                    rx="2"
                                />
                                <path d="M8 11V8a4 4 0 1 1 8 0v3" />
                            </svg>
                            <span>
                                Protected by reCAPTCHA. We respect your privacy.
                            </span>
                        </div>
                    </div>
                </div>
            </div>
        </Dialog>
    )
}

export default HomepageBookCallLeadModal
